// src/utils/verseAlgorithm.js
// Two-layer suggested verses algorithm: Faith Journey Context + Notes Analysis

import { THEME_MAPPINGS, commonWords } from './constants';

// Helper: Normalize to array
const toArray = (val) => Array.isArray(val) ? val : (typeof val === 'string' ? val.split(',').map(t => t.trim()).filter(Boolean) : []);

// Faith Journey Context Score
function calculateContextScore(verse, context) {
  let total = 0;
  let count = 0;
  if (context.ageRange && verse.ageRelevance && verse.ageRelevance[context.ageRange] !== undefined) {
    total += verse.ageRelevance[context.ageRange];
    count++;
  }
  if (context.relationshipStatus && verse.relationshipRelevance && verse.relationshipRelevance[context.relationshipStatus] !== undefined) {
    total += verse.relationshipRelevance[context.relationshipStatus];
    count++;
  }
  if (context.discipleshipStage && verse.discipleshipStageRelevance && verse.discipleshipStageRelevance[context.discipleshipStage] !== undefined) {
    total += verse.discipleshipStageRelevance[context.discipleshipStage];
    count++;
  }
  if (context.christianExperience && verse.christianExperienceRelevance && verse.christianExperienceRelevance[context.christianExperience] !== undefined) {
    total += verse.christianExperienceRelevance[context.christianExperience];
    count++;
  }
  return count > 0 ? total / count : 0;
}

// Notes Analysis Score (weighted)
function calculateNotesScore(verse, notes) {
  if (!notes || notes.length === 0) return 0;
  // Use the most recent note
  const noteText = notes[0]?.note || notes[0];
  if (!noteText) return 0;
  // Ensure noteText is a string before calling toLowerCase
  const noteTextString = typeof noteText === 'string' ? noteText : String(noteText || '');
  if (!noteTextString || noteTextString.trim().length === 0) return 0;
  const normalizedNote = noteTextString.toLowerCase();
  const words = normalizedNote.split(/\W+/).filter(word => word.length > 2 && (!commonWords?.includes(word) || !commonWords));

  // Contextual meaning match (40%)
  const contextual = calculateContextualScoreFromText(normalizedNote, verse.contextualMeaning);
  // Theme match (25%)
  const themeMatch = calculateThemeMatchScore(normalizedNote, verse);
  // Direct text match (20%)
  const textMatch = calculateTextMatchScore(words, verse.text);
  // Tag match (15%)
  const tagMatch = calculateTagMatchScore(normalizedNote, verse);

  return (
    contextual * 0.4 +
    themeMatch * 0.25 +
    textMatch * 0.2 +
    tagMatch * 0.15
  );
}

// Contextual meaning match (for notes analysis)
function calculateContextualScoreFromText(noteText, contextualMeaning) {
  if (!contextualMeaning || !noteText) return 0;
  // Ensure noteText is a string
  const noteTextString = typeof noteText === 'string' ? noteText : String(noteText || '');
  if (!noteTextString || noteTextString.trim().length === 0) return 0;
  const normalizedContext = contextualMeaning.toLowerCase();
  const noteWords = noteTextString.split(/\W+/).filter(word => word.length > 2 && !commonWords.includes(word));
  return noteWords.reduce((score, word) => {
    return normalizedContext.includes(word) ? score + 1 : score;
  }, 0) / Math.max(noteWords.length, 1);
}

// Theme match (for notes analysis)
function calculateThemeMatchScore(noteText, verse) {
  if (!noteText) return 0;
  // Ensure noteText is a string
  const noteTextString = typeof noteText === 'string' ? noteText : String(noteText || '');
  if (!noteTextString || noteTextString.trim().length === 0) return 0;
  let maxScore = 0;
  Object.entries(THEME_MAPPINGS).forEach(([theme, mapping]) => {
    let themeScore = 0;
    mapping.tags.forEach(tag => {
      if (noteTextString.includes(tag.toLowerCase())) themeScore += 0.5;
    });
    mapping.categories.forEach(category => {
      if (noteTextString.includes(category.toLowerCase())) themeScore += 0.3;
    });
    const metadataWeight = verse.themeMetadata?.[theme.toLowerCase()] || 0;
    themeScore *= (1 + metadataWeight);
    maxScore = Math.max(maxScore, themeScore);
  });
  return maxScore;
}

// Direct text match (for notes analysis)
function calculateTextMatchScore(words, verseText) {
  if (!verseText) return 0;
  const normalizedText = verseText.toLowerCase();
  return words.reduce((score, word) => {
    return normalizedText.includes(word) ? score + 1 : score;
  }, 0) / Math.max(words.length, 1);
}

// Tag match (for notes analysis)
function calculateTagMatchScore(noteText, verse) {
  const tagsArr = toArray(verse.tags);
  if (!tagsArr.length || !noteText) return 0;
  // Ensure noteText is a string
  const noteTextString = typeof noteText === 'string' ? noteText : String(noteText || '');
  if (!noteTextString || noteTextString.trim().length === 0) return 0;
  const lowerNoteText = noteTextString.toLowerCase();
  return tagsArr.reduce((score, tag) => {
    return lowerNoteText.includes(tag.toLowerCase()) ? score + 1 : score;
  }, 0) / tagsArr.length;
}

// Helper to generate explanation for each suggestion
function generateExplanation(verse, context, notes, discipleId) {
  let explanation = '';
  if (notes && notes.length > 0) {
    const noteText = notes[0]?.note || notes[0];
    // Ensure noteText is a string before using it
    if (noteText) {
      const noteTextString = typeof noteText === 'string' ? noteText : String(noteText);
      if (noteTextString && noteTextString.trim().length > 0) {
        explanation += `Based on your recent note: "${noteTextString}". `;
      }
    }
  }
  // Humanized Faith Journey context
  const age = context.ageRange ? context.ageRange.toLowerCase() : '';
  const stage = context.discipleshipStage ? context.discipleshipStage.toLowerCase() : '';
  const relationship = context.relationshipStatus ? context.relationshipStatus.toLowerCase() : '';
  const experience = context.christianExperience ? context.christianExperience.toLowerCase() : '';
  let contextSentence = '';
  if (age || stage || relationship || experience) {
    contextSentence = 'Relevant for ';
    if (age) contextSentence += `a ${age} `;
    if (stage) contextSentence += `who is a ${stage} `;
    if (relationship) contextSentence += `that is ${relationship} `;
    if (experience) contextSentence += `and who ${experience}`;
    contextSentence = contextSentence.trim().replace(/\s+/g, ' ');
    if (!contextSentence.endsWith('.')) contextSentence += '.';
  }
  explanation += contextSentence;
  return explanation.trim();
}

// Main export: getSuggestedVerses
export function getSuggestedVerses({ context, notes, availableVerses, count = 7, discipleId }) {
  if (!availableVerses?.length) return [];

  // Notes: expects at most 2 notes (already filtered in caller)
  const [note1, note2] = notes;

  // Score each verse for each note
  const scoredForNote1 = note1 ? availableVerses.map(verse => ({
    verse,
    score: calculateNotesScore(verse, [note1]),
    contextScore: calculateContextScore(verse, context),
    combinedScore: 0 // will set below
  })) : [];
  const scoredForNote2 = note2 ? availableVerses.map(verse => ({
    verse,
    score: calculateNotesScore(verse, [note2]),
    contextScore: calculateContextScore(verse, context),
    combinedScore: 0 // will set below
  })) : [];

  // Combine with context score (50/50)
  scoredForNote1.forEach(item => {
    item.combinedScore = (item.score * 0.5) + (item.contextScore * 0.5);
  });
  scoredForNote2.forEach(item => {
    item.combinedScore = (item.score * 0.5) + (item.contextScore * 0.5);
  });

  // Sort by combined score
  scoredForNote1.sort((a, b) => b.combinedScore - a.combinedScore);
  scoredForNote2.sort((a, b) => b.combinedScore - a.combinedScore);

  // Pick top matches per rules
  let suggestions = [];
  if (note1 && !note2) {
    // Only one note: 2 matches for note, 5 balanced
    suggestions = scoredForNote1.slice(0, 2).map(item => ({
      verse: item.verse,
      contextInfo: {
        ageRange: context.ageRange,
        relationshipStatus: context.relationshipStatus,
        discipleshipStage: context.discipleshipStage,
        christianExperience: context.christianExperience
      },
      explanation: generateExplanation(item.verse, context, [note1], discipleId)
    }));
    // Add 5 balanced
    const usedRefs = new Set(suggestions.map(s => s.verse.reference));
    const balanced = generateBalancedSuggestions(availableVerses.filter(v => !usedRefs.has(v.reference)), context, 5, discipleId);
    suggestions = suggestions.concat(balanced);
  } else if (note1 && note2) {
    // Two notes: 2 for most recent, 1 for previous, 4 balanced
    suggestions = scoredForNote1.slice(0, 2).map(item => ({
      verse: item.verse,
      contextInfo: {
        ageRange: context.ageRange,
        relationshipStatus: context.relationshipStatus,
        discipleshipStage: context.discipleshipStage,
        christianExperience: context.christianExperience
      },
      explanation: generateExplanation(item.verse, context, [note1], discipleId)
    }));
    const usedRefs = new Set(suggestions.map(s => s.verse.reference));
    // 1 for previous note
    const prevMatch = scoredForNote2.find(item => !usedRefs.has(item.verse.reference));
    if (prevMatch) {
      suggestions.push({
        verse: prevMatch.verse,
        contextInfo: {
          ageRange: context.ageRange,
          relationshipStatus: context.relationshipStatus,
          discipleshipStage: context.discipleshipStage,
          christianExperience: context.christianExperience
        },
        explanation: generateExplanation(prevMatch.verse, context, [note2], discipleId)
      });
      usedRefs.add(prevMatch.verse.reference);
    }
    // Add 4 balanced
    const balanced = generateBalancedSuggestions(availableVerses.filter(v => !usedRefs.has(v.reference)), context, 4, discipleId);
    suggestions = suggestions.concat(balanced);
  } else {
    // No notes: just balanced
    suggestions = generateBalancedSuggestions(availableVerses, context, count, discipleId);
  }
  // Always return up to count
  return suggestions.slice(0, count);
}

// Helper: generate balanced suggestions from underrepresented growth areas
function generateBalancedSuggestions(availableVerses, context, count, discipleId) {
  // Group by growth area
  const byArea = {};
  availableVerses.forEach(v => {
    const area = v.growthArea || 'Unknown';
    if (!byArea[area]) byArea[area] = [];
    byArea[area].push(v);
  });
  // Pick one from each area, then fill in
  const result = [];
  Object.values(byArea).forEach(areaVerses => {
    if (areaVerses.length) result.push(areaVerses[0]);
  });
  // Fill in with remaining
  const usedRefs = new Set(result.map(v => v.reference));
  const remaining = availableVerses.filter(v => !usedRefs.has(v.reference));
  result.push(...remaining.slice(0, count - result.length));
  // Attach contextInfo and explanation
  return result.slice(0, count).map(verse => ({
    verse,
    contextInfo: {
      ageRange: context.ageRange,
      relationshipStatus: context.relationshipStatus,
      discipleshipStage: context.discipleshipStage,
      christianExperience: context.christianExperience
    },
    explanation: generateExplanation(verse, context, [], discipleId)
  }));
}

// Optionally, export helpers for testing
export const _testHelpers = {
  calculateContextScore,
  calculateNotesScore,
  calculateContextualScoreFromText,
  calculateThemeMatchScore,
  calculateTextMatchScore,
  calculateTagMatchScore,
}; 